import { useState, useEffect, useRef } from 'react';

interface UseLazySectionOptions {
  rootMargin?: string;
  threshold?: number;
  triggerOnce?: boolean;
}

/**
 * Hook for viewport-based lazy loading of sections.
 * Only triggers load when the placeholder enters the viewport.
 */
export const useLazySection = (options: UseLazySectionOptions = {}) => {
  const { 
    rootMargin = '200px', // Load 200px before entering viewport
    threshold = 0,
    triggerOnce = true 
  } = options;
  
  const [shouldLoad, setShouldLoad] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasLoaded && triggerOnce) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          setHasLoaded(true);
          if (triggerOnce && ref.current) {
            observer.unobserve(ref.current);
          }
        }
      },
      { rootMargin, threshold }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [rootMargin, threshold, triggerOnce, hasLoaded]);

  return { ref, shouldLoad };
};
